import { AuthStorage } from "utils/auth-storage";
import React, { useEffect, useState } from "react";
import { InboxOutlined } from "@ant-design/icons";
import { Modal, Upload, message } from "antd";
import * as XLSX from "xlsx";
import { useParams } from "react-router-dom";
import { ProjectServices } from "apis/Services/ProjectService";
import ReviewProcurementLogFile from "./review-procurement-log-file";
import {
  mapProcurementImportRowForApi,
  normalizeProcurementImportRow,
  normalizeProcurementImportRows,
  validateProcurementImportHeaders,
} from "./procurementImportUtils";

const { Dragger } = Upload;

function ImportProcurementLog({
  open,
  onCancel,
  onImportSuccess = () => {},
  beginSyncActivity,
  finishSyncActivity,
  failSyncActivity,
}) {
  const { company_id, id } = useParams();
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [acceptedData, setAcceptedData] = useState([]);
  const [rejectedData, setRejectedData] = useState([]);
  const [fileList, setFileList] = useState([]);

  const resetState = () => {
    setIsReviewModalOpen(false);
    setAcceptedData([]);
    setRejectedData([]);
    setFileList([]);
  };

  const handleClose = () => {
    resetState();
    onCancel?.();
  };

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);

  const handleUpload = (file) => {
    const isCsvUpload =
      file?.type === "text/csv" ||
      String(file?.name || "")
        .toLowerCase()
        .endsWith(".csv");
    const reader = new FileReader();

    reader.onload = (event) => {
      const syncToken = beginSyncActivity?.("Reviewing procurement log upload");

      try {
        const workbook = isCsvUpload
          ? XLSX.read(event.target.result, {
              type: "string",
              raw: true,
            })
          : XLSX.read(new Uint8Array(event.target.result), {
              type: "array",
              cellDates: true,
            });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const headerRow = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          range: 0,
          defval: "",
          raw: true,
        })[0];
        const headerValidation = validateProcurementImportHeaders(headerRow);

        if (!headerValidation.isValid) {
          const validationMessages = [];

          if (headerValidation.missingColumns.length > 0) {
            validationMessages.push(
              `Missing columns: ${headerValidation.missingColumns.join(", ")}`,
            );
          }

          if (headerValidation.unexpectedColumns.length > 0) {
            validationMessages.push(
              `Unexpected columns: ${headerValidation.unexpectedColumns.join(", ")}`,
            );
          }

          message.error("File doesn't match the procurement log template.");
          validationMessages.forEach((validationMessage) =>
            console.error(validationMessage),
          );
          failSyncActivity?.(
            syncToken,
            "Failed to review the procurement log file. The file does not match the expected template.",
          );
          return;
        }

        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          defval: "",
          raw: isCsvUpload,
          dateNF: isCsvUpload ? undefined : "mm-dd-yyyy",
        });
        const mappedRows = jsonData.map((row) =>
          mapProcurementImportRowForApi(normalizeProcurementImportRow(row)),
        );
        const requestData = {
          company_id,
          project_id: id,
          code: AuthStorage.getItem("token"),
          loginEmail: AuthStorage.getItem("email"),
          saveFlag: "0",
          submittalRows: mappedRows,
        };

        ProjectServices.saveUploadedProcurementLog(requestData)
          .then((response) => {
            setAcceptedData(
              normalizeProcurementImportRows(response?.validItems),
            );
            setRejectedData(
              normalizeProcurementImportRows(response?.invalidItems),
            );
            setIsReviewModalOpen(true);
            setFileList([]);
            message.success("File processed successfully!");
            finishSyncActivity?.(syncToken, "Procurement log file reviewed");
          })
          .catch((error) => {
            console.error(error);
            message.error(
              error?.message ||
                "Failed to process the procurement log file. Please try again.",
            );
            failSyncActivity?.(
              syncToken,
              "Failed to review the procurement log file. You may no longer be synced to the latest version.",
            );
          });
      } catch (error) {
        console.error("Error processing file:", error);
        message.error("Error processing file. Please try again.");
        failSyncActivity?.(
          syncToken,
          "Failed to review the procurement log file. You may no longer be synced to the latest version.",
        );
      }
    };

    reader.onerror = () => {
      console.error("Failed to read the uploaded procurement log file.");
      message.error("Failed to read the uploaded file.");
    };

    if (isCsvUpload) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
    return false;
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    fileList,
    accept: ".xlsx,.xls,.csv",
    beforeUpload: (file) => {
      const isExcelOrCsv =
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel" ||
        file.type === "text/csv" ||
        file.name.toLowerCase().endsWith(".csv");

      if (!isExcelOrCsv) {
        message.error("You can only upload Excel or CSV files!");
        return Upload.LIST_IGNORE;
      }

      setFileList([file]);
      handleUpload(file);
      return false;
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  return (
    <>
      <Modal
        title="Upload Procurement Log"
        centered
        open={open && !isReviewModalOpen}
        onCancel={handleClose}
        footer={null}
        width={600}
      >
        <div className="py-1">
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ fontSize: "48px", color: "#1890ff" }} />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Support for Excel (.xlsx, .xls) and CSV files
            </p>
          </Dragger>

          <div className="mt-4 text-center">
            <p>
              <a
                href="/koncurentSampleProcurementLog_template.csv"
                download="koncurentSampleProcurementLog_template.csv"
              >
                Download Template
              </a>
            </p>
          </div>
        </div>
      </Modal>

      <Modal
        title="Review & finalize"
        centered
        open={open && isReviewModalOpen}
        onCancel={handleClose}
        footer={null}
        width={1500}
      >
        <ReviewProcurementLogFile
          rejectedData={rejectedData}
          acceptedData={acceptedData}
          handleCancel={handleClose}
          setAcceptedData={setAcceptedData}
          setRejectedData={setRejectedData}
          onImportSuccess={onImportSuccess}
          beginSyncActivity={beginSyncActivity}
          finishSyncActivity={finishSyncActivity}
          failSyncActivity={failSyncActivity}
        />
      </Modal>
    </>
  );
}

export default ImportProcurementLog;
